import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, Input, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { palette } from '../app/theme';

const initials = (s: string) =>
  s
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('') || '?';

/** Avatar (photo, else initials or icon), a bold name and a muted second line, as in the reference tables. `copyable` adds a copy-name button, `copySub` a button copying that text after the second line. `lines` lets the name wrap onto up to that many lines instead of one. */
export function NameCell({ name, sub, to, icon, image, square, copyable, copySub, lines, size = 36 }: { name: string; sub?: ReactNode; to?: string; icon?: ReactNode; image?: string; square?: boolean; copyable?: boolean; copySub?: string; lines?: number; size?: number }) {
  const title = to ? <Link to={to} style={{ color: palette.text, fontWeight: 600 }}>{name}</Link> : <Typography.Text strong>{name}</Typography.Text>;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
      <Avatar
        size={size}
        src={image}
        shape={square ? 'square' : 'circle'}
        icon={image ? undefined : icon}
        style={{ background: image && square ? '#fff' : palette.primarySoft, color: palette.primary, fontWeight: 600, fontSize: size * 0.36, flex: 'none', border: image && square ? `1px solid ${palette.borderStrong}` : undefined }}
      >
        {icon || image ? null : initials(name)}
      </Avatar>
      <div style={{ minWidth: 0, lineHeight: 1.35 }}>
        <div style={lines ? { overflow: 'hidden', display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: lines, overflowWrap: 'anywhere' } : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={lines ? name : undefined}>
          {title}
          {copyable && <Typography.Text copyable={{ text: name }} />}
        </div>
        {sub && (
          <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <Typography.Text type="secondary" style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</Typography.Text>
            {copySub && <Typography.Text type="secondary" copyable={{ text: copySub }} style={{ fontSize: 13, flex: 'none' }} />}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Toolbar search field in the reference style (icon inside, no attached button). Searches on Enter
 * and when cleared; `value` re-seeds it when the URL search changes.
 */
export function TableSearch({ value, onSearch, placeholder = 'Search', width = 320 }: { value?: string; onSearch: (q: string) => void; placeholder?: string; width?: number }) {
  return (
    <Input
      key={value ?? ''}
      allowClear
      defaultValue={value}
      placeholder={placeholder}
      aria-label={placeholder}
      prefix={<SearchOutlined style={{ color: palette.textTertiary }} />}
      onPressEnter={(e) => onSearch((e.target as HTMLInputElement).value.trim())}
      onChange={(e) => {
        if (!e.target.value) onSearch('');
      }}
      style={{ width, maxWidth: '100%' }}
    />
  );
}
